import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    TextField,
} from "@material-ui/core";
import { Add, Remove, Close } from "@material-ui/icons";
import React, { useState } from "react";
import CSVUploadModal from "./VesselIndexCsvUpload";
import { Autocomplete } from "@material-ui/lab";
import { toastError } from "../../../utils";

const InsertIndexModel = (props) => {
    const {
        handleClose,
        open,
        onSubmit = () => { },
        indexValue,
        setIndexValue = () => { },
        subLoader,
        isCsvUploaded,
        setIsCsvUploaded,
        title,
        vesselIndexesData,
        buttonTitle = "ADD",
        indexList,
        setSelectedIndex = () => { },
        selectedIndex,
    } = props;
    const [errors, setErrors] = useState({});
    const [insertType, setInsertType] = useState('csv');

    const handleManualInputChange = (index, field, value) => {
        const newInputs = [...indexValue];
        newInputs[index][field] = String(field) === 'indexValue' ? Number(value) : value;
        setIndexValue(newInputs);
        setErrors((prev) => ({ ...prev, indexValue: '' }));
    };

    const handleAddManualInput = () => {
        setIndexValue([...indexValue, { indexValue: 0, Date: "" }]);
    };

    const handleRemoveManualInput = (index) => {
        const newInputs = indexValue.filter((_, i) => i !== index);
        setIndexValue(newInputs);
    };

    const validateForm = () => {
        const newErrors = {};
        if (indexValue.some((input) => !input.indexValue || !input.Date)) {
            newErrors.indexValue = "All index values and dates must be filled";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!isCsvUploaded) {
            if (!validateForm()) return;
            const payload = {
                indexValues: indexValue.map((input) => ({
                    Value: input.indexValue,
                    Date: new Date(input?.Date)?.toISOString() || null,
                })),
            };
            onSubmit(payload);
            return;
        } else {
            if (!indexValue || indexValue.length === 0) {
                toastError("No index data to submit");
                return;
            }

            const groupedPayload = indexValue.reduce((acc, row) => {
                if (!row.Date) return acc;

                const isoDate = new Date(row.Date).toISOString();

                Object.keys(row).forEach((key) => {
                    if (key === "Date") return;

                    const value = row[key];

                    if (value === undefined || value === null || value === "") return;

                    if (!acc[key]) {
                        acc[key] = [];
                    }

                    acc[key].push({
                        Value: Number(value),
                        Date: isoDate,
                    });
                });

                return acc;
            }, {});

            onSubmit(groupedPayload);
        }
    };

    const handleCsv = (data) => {
        setIsCsvUploaded(true);
        setIndexValue(data);
    };

    const ModelClose = () => {
        setIndexValue([{ indexValue: 0, Date: "" }]);
        setErrors({});
        handleClose();
        setSelectedIndex('');
    };

    const isEmptyIndexDetails =
        indexValue.length && indexValue.some((i) => !i.indexValue || !i.Date);

    return (
        <Dialog
            open={open}
            onClose={ModelClose}
            fullWidth={true}
            maxWidth="sm"
            className="superadmin-dialog"
            aria-labelledby="insert-market-index-title"
            PaperProps={{
                style: {
                    borderRadius: "0px",
                    maxHeight: "90vh",
                },
            }}
        >
            {/* Header */}
            <DialogTitle className="!py-1.5 !px-3 bg-slate-50 border-b border-slate-200 !m-0">
                <div className="flex justify-between items-center">
                    <h2 id="insert-market-index-title" className="text-[14px] font-medium text-slate-900">{title || "Insert Data"}</h2>
                    <IconButton
                        onClick={ModelClose}
                        aria-label="Close dialog"
                        size="small"
                        className="text-slate-400 hover:text-slate-600 !p-0.5"
                    >
                        <Close style={{ fontSize: "16px" }} />
                    </IconButton>
                </div>
            </DialogTitle>

            {/* Body Content */}
            <DialogContent className="admin-form !py-3 !px-3 space-y-2.5 w-full" style={{ scrollbarWidth: "thin" }}>
                {/* Select Index Dropdown */}
                {insertType === "manual" && (
                    <div className="mb-2">
                        <label htmlFor="manual-market-index" className="block text-[11px] font-medium text-slate-800 mb-0.5">
                            Select Index
                        </label>
                        <Autocomplete
                            id="manual-market-index"
                            size="small"
                            options={(indexList || []).filter((option) => option.isOwner)}
                            getOptionLabel={(option) => option?.indexName || ""}
                            getOptionSelected={(option, value) => option.indexName === value.indexName}
                            value={indexList?.find((option) => option.indexName === selectedIndex) || null}
                            onChange={(_, option) => setSelectedIndex(option?.indexName || "")}
                            noOptionsText="No indexes found"
                            classes={{ option: "!text-[11px] !py-1 !min-h-[28px]", paper: "compact-popover !rounded-none" }}
                            renderInput={(params) => (
                                <TextField {...params} variant="outlined" size="small" placeholder="Search your indexes" />
                            )}
                        />
                    </div>
                )}

                {/* Input Type Selection */}
                <div>
                    <label className="block text-[11px] font-medium text-slate-800 mb-0.5">
                        Input Type
                    </label>
                    <RadioGroup
                        row
                        aria-label="Index input format"
                        value={insertType}
                        onChange={(e) => setInsertType(e.target.value)}
                        className="!m-0"
                    >
                        <FormControlLabel
                            value="csv"
                            control={<Radio size="small" color="primary" className="!p-1" />}
                            label={<span className="text-[11px] font-medium text-slate-800">CSV Upload</span>}
                            className="!mr-3 !my-0"
                        />
                        <FormControlLabel
                            value="manual"
                            control={<Radio size="small" color="primary" className="!p-1" />}
                            label={<span className="text-[11px] font-medium text-slate-800">Manual Entry</span>}
                            className="!mr-0 !my-0"
                        />
                    </RadioGroup>
                </div>

                {/* CSV Content */}
                {insertType === "csv" && (
                    <div className="w-full bg-white p-2.5 rounded-none border border-slate-200">
                        <CSVUploadModal onUpload={handleCsv} vesselIndexesData={vesselIndexesData} />
                    </div>
                )}

                {/* Manual Content */}
                {insertType === "manual" && (
                    <div className="space-y-2 mb-2">
                        <div className="flex flex-col gap-1.5 overflow-y-auto border border-slate-200 rounded-none p-1.5 bg-slate-50" style={{ maxHeight: "180px" }}>
                            {indexValue.map((input, index) => (
                                <div key={index} className="flex gap-2 items-center bg-white p-1.5 border border-slate-200 rounded-none">
                                    <div className="flex-1">
                                        <TextField
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            placeholder="Index Value"
                                            type="number"
                                            value={input.indexValue}
                                            onChange={(e) =>
                                                handleManualInputChange(index, "indexValue", e.target.value)
                                            }
                                            error={!!errors.indexValue}
                                            InputProps={{ style: { borderRadius: "0px" } }}
                                            inputProps={{ "aria-label": `Index value ${index + 1}`, style: { padding: "4px 6px", fontSize: "11px", fontWeight: "500" } }}
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            type="date"
                                            size="small"
                                            value={input.Date}
                                            onChange={(e) =>
                                                handleManualInputChange(index, "Date", e.target.value)
                                            }
                                            InputLabelProps={{ shrink: true }}
                                            error={!!errors.indexValue}
                                            InputProps={{ style: { borderRadius: "0px" } }}
                                            inputProps={{ "aria-label": `Date for value ${index + 1}`, style: { padding: "4px 6px", fontSize: "11px", fontWeight: "500" } }}
                                        />
                                    </div>
                                    {indexValue.length > 1 && (
                                        <IconButton
                                            onClick={() => handleRemoveManualInput(index)}
                                            aria-label={`Remove value ${index + 1}`}
                                            size="small"
                                            className="text-slate-400 hover:text-slate-600 !p-0.5"
                                        >
                                            <Remove style={{ fontSize: "16px" }} />
                                        </IconButton>
                                    )}
                                </div>
                            ))}
                        </div>

                        {errors.indexValue && (
                            <p className="text-rose-500 text-[11px] font-medium px-1">{errors.indexValue}</p>
                        )}

                        <Button
                            startIcon={<Add style={{ fontSize: "14px" }} />}
                            onClick={handleAddManualInput}
                            variant="outlined"
                            color="primary"
                            disabled={isEmptyIndexDetails}
                            size="small"
                            className="!text-[11px] !font-medium !h-7 !py-0 !px-2 !normal-case !rounded-none"
                        >
                            Add Row
                        </Button>
                    </div>
                )}
            </DialogContent>

            {/* Footer */}
            <DialogActions className="!py-1.5 !px-3 border-t border-slate-200 bg-slate-50">
                <div className="flex justify-end gap-2 w-full">
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={ModelClose}
                        className="!text-[11px] !font-medium text-slate-700 border-slate-300 hover:bg-slate-100 !h-7 !py-0 !px-3 !normal-case !rounded-none"
                    >
                        CANCEL
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        disabled={subLoader}
                        onClick={handleSave}
                        className="!text-[11px] !font-medium !bg-[#313C4C] text-white hover:!bg-[#1e2632] !h-7 !py-0 !px-3 !normal-case !rounded-none"
                    >
                        {subLoader ? (
                            <CircularProgress className="text-white" size={16} />
                        ) : (
                            buttonTitle || "ADD"
                        )}
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
};

export default InsertIndexModel;
